// AccessReviewsCard — Home queue for the member's access-review campaigns,
// styled like the Tasks card. Each row mirrors a task row but shows:
//   Status badge (same as the Access Reviews page) · Name · Due date ·
//   percent-complete radial · View button (opens the campaign sidepanel on the
//   Access Reviews page, via its /campaigns/:id route).
//
// Shows active (non-completed) campaigns, newest first; self-hides when empty.
// Each row fetches its own campaign details (non-suspense, no error boundary)
// for the completion % + the derived "Awaiting approval" status — a failed
// detail fetch just drops that row's radial rather than breaking the card.

import { Box, Flex, HStack, Icon, Text } from "@chakra-ui/react";
import { format } from "date-fns";
import { useCallback, useEffect, useState } from "react";
import { MailCheck } from "lucide-react";

import Card from "components/ds/Card";
import CardHeader from "components/ds/CardHeader";
import { MF_ACCESS_REVIEWS, type MFCampaign } from "state/members/accessReviews";

import CampaignStatusBadge from "../AccessReviews/campaigns/components/CampaignStatusBadge";
import { OverdueBlip } from "../AccessReviews/campaigns/components/CampaignMeta";
import {
  buildHomeStrip,
  type CampaignQuerySignal,
  type HomeStripEntry,
} from "../AccessReviews/campaigns/reviewLogic";

// Small donut + "{pct}%" — the percent-complete radial. Mirrors ProgressPill's
// SVG approach (track + arc via currentColor) but shows a percentage.
const RadialProgress = ({ pct }: { pct: number }) => {
  const clamped = Math.max(0, Math.min(100, pct));
  const complete = clamped >= 100;
  const size = 22;
  const stroke = 3;
  const r = (size - stroke) / 2;
  const circ = 2 * Math.PI * r;
  const c = size / 2;

  return (
    <HStack spacing="1xs" flexShrink={0}>
      <Box
        as="svg"
        width={`${size}px`}
        height={`${size}px`}
        viewBox={`0 0 ${size} ${size}`}
        color={complete ? "success.500" : "p.500"}
        flexShrink={0}
      >
        <Box
          as="circle"
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeOpacity={0.18}
          strokeWidth={stroke}
        />
        <Box
          as="circle"
          cx={c}
          cy={c}
          r={r}
          fill="none"
          stroke="currentColor"
          strokeWidth={stroke}
          strokeLinecap="round"
          strokeDasharray={circ}
          strokeDashoffset={circ * (1 - clamped / 100)}
          transform={`rotate(-90 ${c} ${c})`}
        />
      </Box>
      <Box
        as="span"
        fontSize="smd"
        fontWeight={600}
        color={complete ? "success.700" : "neutral.800"}
        sx={{ fontVariantNumeric: "tabular-nums" }}
      >
        {Math.round(clamped)}%
      </Box>
    </HStack>
  );
};

// Trailing affordance — the whole row is the click target (see CampaignRow), so
// this is a visual cue only (not a nested button, which would be a click-within-
// click). It still highlights on row hover via the group.
const ViewButton = () => (
  <Box
    flexShrink={0}
    px="10px"
    py="1xs"
    fontSize="smd"
    fontWeight={500}
    color="p.600"
    bg="neutral.25"
    borderRadius="component.lg"
    borderWidth="1px"
    borderStyle="solid"
    borderColor="neutral.200"
    transition="background-color 120ms, border-color 120ms"
    _groupHover={{ bg: "neutral.50", borderColor: "neutral.300" }}
  >
    View
  </Box>
);

const ROW_PROPS = {
  align: "center" as const,
  gap: "lg" as const, // 16px
  p: "sm" as const, // 8px
  borderRadius: "component.lg" as const,
  minH: "48px",
};

const CampaignRow = ({
  campaign,
  onView,
  onQuerySignal,
}: {
  campaign: MFCampaign;
  onView: () => void;
  /** report this campaign's answered-query count up for the Home strip */
  onQuerySignal?: (campaignId: string, answeredQueries: number) => void;
}) => {
  const { data: details } = MF_ACCESS_REVIEWS.useGetCampaignDetails(
    campaign.id,
    undefined,
    { useErrorBoundary: false },
  );

  const stats = details?.stats;

  // Bubble the answered-query count up to the card once details resolve, so
  // the attention strip can aggregate across campaigns (PLO-4816).
  const answeredQueries =
    stats?.answered_queries ?? stats?.accounts?.answered_queries ?? 0;
  useEffect(() => {
    if (details) onQuerySignal?.(campaign.id, answeredQueries);
  }, [details, campaign.id, answeredQueries, onQuerySignal]);
  // All reviews done but the campaign isn't closed yet → awaiting approval
  // (matches the Access Reviews page). Only once details have loaded.
  const reviewsDone =
    !!stats && stats.total_reviews > 0 && stats.completed_reviews === stats.total_reviews;
  const status =
    reviewsDone && campaign.status !== "completed"
      ? "awaiting_approval"
      : campaign.status;

  const pct = stats
    ? stats.completion_percentage ??
      (stats.total_reviews > 0
        ? (stats.completed_reviews / stats.total_reviews) * 100
        : 0)
    : 0;

  return (
    <Flex
      {...ROW_PROPS}
      role="group"
      cursor="pointer"
      transition="background-color 120ms"
      _hover={{ bg: "bg.hover" }}
      onClick={onView}
    >
      {/* Fixed-width status column so rows line up with each other and with the
          Tasks card's 160px type column above. */}
      <Box w="160px" flexShrink={0}>
        <CampaignStatusBadge
          status={status}
          dueDate={campaign.dueDate}
          startedDate={campaign.insertedAt}
          truncate
        />
      </Box>
      <Box flex="1" minW={0}>
        <Text fontSize="md" fontWeight={500} color="neutral.950" noOfLines={1}>
          {campaign.name}
        </Text>
      </Box>
      <Box flexShrink={0} fontSize="smd" color="neutral.700" whiteSpace="nowrap">
        Due {formatDue(campaign.dueDate)}
      </Box>
      <RadialProgress pct={pct} />
      <ViewButton />
    </Flex>
  );
};

const formatDue = (value: string): string => {
  try {
    return format(new Date(value), "d MMM yyyy");
  } catch {
    return "-";
  }
};

// A single attention pill in the Home strip (overdue or answered-queries).
// Clicking it jumps into the linked campaign so the reviewer lands where the
// work is (PLO-4816).
const StripPill = ({
  entry,
  onOpen,
}: {
  entry: HomeStripEntry;
  onOpen: (campaignId: string) => void;
}) => {
  const isOverdue = entry.kind === "overdue";
  return (
    <HStack
      as="button"
      spacing="1xs"
      px="8px"
      py="4px"
      borderRadius="component.lg"
      borderWidth="1px"
      borderStyle="solid"
      borderColor={isOverdue ? "error.200" : "p.200"}
      bg={isOverdue ? "error.50" : "p.50"}
      transition="background-color 120ms, border-color 120ms"
      _hover={{
        bg: isOverdue ? "error.100" : "p.100",
        borderColor: isOverdue ? "error.300" : "p.300",
      }}
      onClick={() => onOpen(entry.campaignId)}
      flexShrink={0}
    >
      {isOverdue ? (
        <OverdueBlip />
      ) : (
        <Icon as={MailCheck} boxSize="14px" color="p.600" />
      )}
      <Text
        fontSize="smd"
        fontWeight={600}
        color={isOverdue ? "error.700" : "p.700"}
        whiteSpace="nowrap"
      >
        {isOverdue
          ? "Overdue"
          : `${entry.count} ${entry.count === 1 ? "query" : "queries"} answered`}
      </Text>
    </HStack>
  );
};

export interface AccessReviewsCardProps {
  onViewAll: () => void;
  onOpenCampaign: (id: string) => void;
}

/** Newest first. Shared with Home so both observers hit one query key. */
export const CAMPAIGN_FILTERS = {
  sort_by: "inserted_at",
  sort_direction: "desc",
} as const;

/** The design caps the Home strip at five campaigns. */
const MAX_CAMPAIGNS = 5;

/**
 * The campaigns this card renders from: active ones, newest first.
 *
 * Exported because Home decides both its grid layout and whether to show the
 * dashboard empty state, and it cannot see whether this card returns null
 * without asking the same question of the same data.
 */
export const activeCampaigns = (
  data: MFCampaign[] | Record<string, unknown> | undefined,
): MFCampaign[] =>
  (Array.isArray(data) ? data : []).filter((c) => c.status !== "completed");

const AccessReviewsCard = ({ onViewAll, onOpenCampaign }: AccessReviewsCardProps) => {
  const { data } = MF_ACCESS_REVIEWS.useListCampaigns(CAMPAIGN_FILTERS);

  // Per-campaign answered-query counts, reported up by each row as its details
  // resolve. Keyed by campaign id so late/again reports overwrite cleanly.
  const [querySignals, setQuerySignals] = useState<
    Record<string, number>
  >({});
  const handleQuerySignal = useCallback(
    (campaignId: string, answeredQueries: number) => {
      setQuerySignals((prev) =>
        prev[campaignId] === answeredQueries
          ? prev
          : { ...prev, [campaignId]: answeredQueries },
      );
    },
    [],
  );

  // No group_by → a flat array. Show active (non-completed) campaigns only.
  const active = activeCampaigns(data);
  const shown = active.slice(0, MAX_CAMPAIGNS);

  // Mirror the Tasks card: no outstanding reviews → the card doesn't show.
  if (shown.length === 0) return null;

  // Attention strip: overdue campaigns + answered queries needing action.
  // Scoped to the campaigns actually rendered (`shown` = active.slice(0, 5)),
  // because only those rows mount a CampaignRow and report their answered-query
  // count up — aggregating over all `active` would fold in phantom zeros for the
  // un-rendered tail. A true full-fleet answered-query count would need the stat
  // on the campaign-list payload itself (out of scope here).
  const signals: CampaignQuerySignal[] = shown.map((c) => ({
    campaignId: c.id,
    answeredQueries: querySignals[c.id] ?? 0,
  }));
  const strip = buildHomeStrip(
    shown.map((c) => ({ id: c.id, status: c.status, dueDate: c.dueDate })),
    signals,
  );

  return (
    <Card variant="tasks">
      <CardHeader
        title="Access Reviews"
        count={active.length}
        action={{ label: "View all", onClick: onViewAll }}
      />
      {strip.entries.length > 0 && (
        <HStack spacing="sm" mb="md" flexWrap="wrap" px="1xs">
          {strip.entries.map((entry) => (
            <StripPill
              key={`${entry.kind}-${entry.campaignId}`}
              entry={entry}
              onOpen={onOpenCampaign}
            />
          ))}
        </HStack>
      )}
      <Box
        bg="neutral.25"
        borderWidth="1px"
        borderStyle="solid"
        borderColor="neutral.200"
        borderRadius="component.2xl"
        overflow="hidden"
        p="sm"
        maxH="264px"
        overflowY="auto"
        display="flex"
        flexDirection="column"
        gap="md"
        sx={{
          scrollbarWidth: "thin",
          "&::-webkit-scrollbar": { width: "8px" },
          "&::-webkit-scrollbar-thumb": {
            background: "var(--chakra-colors-neutral-300)",
            borderRadius: "8px",
          },
        }}
      >
        {shown.map((c) => (
          <CampaignRow
            key={c.id}
            campaign={c}
            onView={() => onOpenCampaign(c.id)}
            onQuerySignal={handleQuerySignal}
          />
        ))}
      </Box>
    </Card>
  );
};

export default AccessReviewsCard;
